import type { JSX } from 'react';
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { formatDate, defaultDateRange } from '@/lib/utils';
import { userFilterOptions } from '@/lib/user-filter-options';
import { Select } from '@/components/shared/select';
import type {
  ConversationTurnDetail,
  ConversationTurnPart,
  ConversationTurnSummary,
} from '@echo-work-platform/shared-types';

// datetime-local (no timezone) → ISO-8601 the backend's z.string().datetime() accepts.
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function PartView({ part }: { part: ConversationTurnPart }): JSX.Element {
  if (part.type !== 'tool') {
    return (
      <div className="rounded border border-slate-200 bg-white p-2 text-xs">
        <span className="font-mono text-slate-400">{part.type}</span>
      </div>
    );
  }
  return (
    <div className="rounded border border-slate-200 bg-white p-3 text-xs">
      <div className="mb-1 flex items-center gap-2">
        <span className="font-mono font-semibold">{part.tool}</span>
        {part.error && (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">hiba</span>
        )}
      </div>
      {part.input !== undefined && (
        <pre className="mt-1 overflow-auto rounded bg-slate-50 p-2 font-mono text-[11px]">
          {typeof part.input === 'string' ? part.input : JSON.stringify(part.input, null, 2)}
        </pre>
      )}
      {part.output && (
        <pre className="mt-1 overflow-auto rounded bg-slate-900 p-2 font-mono text-[11px] text-slate-100">
          {part.output}
        </pre>
      )}
      {part.error && (
        <pre className="mt-1 overflow-auto rounded bg-red-50 p-2 font-mono text-[11px] text-red-800">
          {part.error}
        </pre>
      )}
    </div>
  );
}

function TurnDetail({ id }: { id: string }): JSX.Element {
  const detail = useQuery({
    queryKey: ['/api/admin/conversations', id],
    queryFn: () => api.get<ConversationTurnDetail>(`/api/admin/conversations/${id}`),
  });

  if (detail.isLoading) return <div className="px-4 py-3 text-xs text-slate-500">Betöltés…</div>;
  if (!detail.data) return <div className="px-4 py-3 text-xs text-slate-500">Nem található.</div>;
  const d = detail.data;

  return (
    <div className="space-y-3 px-4 py-3 text-xs">
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Felhasználói üzenet
        </div>
        <div className="whitespace-pre-wrap break-words rounded border border-slate-200 bg-white p-3">
          {d.userText || <span className="text-slate-400">(üres)</span>}
        </div>
      </div>
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Modell válasza
        </div>
        <div className="whitespace-pre-wrap break-words rounded border border-brand-200 bg-brand-50/40 p-3">
          {d.assistantText || <span className="text-slate-400">(nincs szöveges válasz)</span>}
        </div>
      </div>
      {d.parts.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Tool hívások ({d.toolCallCount})
          </div>
          <div className="space-y-2">
            {d.parts
              .filter((p) => p.type === 'tool')
              .map((p, i) => (
                <PartView key={p.callID ?? `part-${i}`} part={p} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Conversations(): JSX.Element {
  const defaults = useMemo(() => defaultDateRange(), []);
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [expanded, setExpanded] = useState<string | null>(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (userId) p.set('userId', userId);
    const fromIso = toIso(from);
    const toIsoVal = toIso(to);
    if (fromIso) p.set('from', fromIso);
    if (toIsoVal) p.set('to', toIsoVal);
    p.set('limit', '200');
    return p.toString();
  }, [userId, from, to]);

  const turns = useQuery({
    queryKey: ['/api/admin/conversations-list', qs],
    queryFn: () => api.get<ConversationTurnSummary[]>(`/api/admin/conversations?${qs}`),
  });

  const userOptions = useMemo(() => userFilterOptions(turns.data, userId), [turns.data, userId]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Beszélgetések</h2>

      <div className="card flex flex-wrap items-end gap-4 p-4">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Felhasználó
          <Select value={userId} onChange={setUserId} options={userOptions} className="w-56" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Ettől
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input w-52"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Eddig
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input w-52"
          />
        </label>
        <button
          className="btn-secondary"
          onClick={() => {
            setUserId('');
            setFrom(defaults.from);
            setTo(defaults.to);
          }}
        >
          Szűrők törlése
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Időpont</th>
              <th className="px-4 py-2 text-left">Felhasználó</th>
              <th className="px-4 py-2 text-left">Modell</th>
              <th className="px-4 py-2 text-left">Üzenet</th>
              <th className="px-4 py-2 text-left">Válasz</th>
              <th className="px-4 py-2 text-left">Tool</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {turns.data?.map((t) => {
              const isOpen = expanded === t.id;
              return (
                <Fragment key={t.id}>
                  <tr
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : t.id)}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(t.occurredAt)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {t.userEmail ?? t.userId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{t.modelName ?? '—'}</td>
                    <td className="max-w-xs truncate px-4 py-2 text-slate-700">{t.userTextPreview}</td>
                    <td className="max-w-xs truncate px-4 py-2 text-slate-500">{t.assistantTextPreview}</td>
                    <td className="px-4 py-2 font-mono text-xs">{t.toolCallCount}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="p-0">
                        <TurnDetail id={t.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {turns.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Nincs a szűrőnek megfelelő beszélgetés.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
